import { Component, OnChanges, Input, Output, EventEmitter, OnDestroy, ViewChild, ElementRef, SimpleChanges } from "@angular/core";
import {COMMA, ENTER} from '@angular/cdk/keycodes';
import * as _ from 'lodash';
import { TenantService } from "../services/tenant.service";
import { of, Subject } from "rxjs";
import { takeUntil, tap } from "rxjs/operators";
import { FormControl } from "@angular/forms";
import { MatChipInputEvent } from '@angular/material/chips';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

@Component({
    selector: 'topic-tag-manager',
    template: `
        <mat-form-field fxFlex>
            <mat-chip-list #chipList>
                <mat-chip *ngFor="let tagOrId of currentTags" color="primary" (removed)="removeTag(tagOrId)">
                    {{ !tagOrId?.isNew ? tagMap[tagOrId]?.label : tagOrId.label }}
                    <mat-icon matChipRemove>cancel</mat-icon>
                </mat-chip>
                <input #tagInputCtrl
                       [formControl]="tagCtrl"
                       [matAutocomplete]="auto"
                       [matChipInputFor]="chipList"
                       [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
                       (matChipInputTokenEnd)="add($event)">
            </mat-chip-list>
            <mat-autocomplete #auto="matAutocomplete" (optionSelected)="selected($event)">
                <mat-option [value]="tag" *ngFor="let tag of filteredTags">{{tag.label}}</mat-option>
            </mat-autocomplete>
        </mat-form-field>
    `
})
export class TopicTagManagerComponent implements OnChanges, OnDestroy {
    @Input() topic: any;
    @Input() tagsUpdatedAt: Date;
    @Input() isShared: boolean;
    @Output() changed = new EventEmitter();
    @ViewChild('tagInputCtrl') tagInput: ElementRef<HTMLInputElement>;
    separatorKeysCodes: number[] = [ENTER, COMMA];
    tagCtrl = new FormControl();
    streamTags: any;
    tagMap: any = {};
    tagPanelOpen: boolean;
    filteredTags = [];
    cleanupTime = new Subject<void>();

    constructor(private tenantService: TenantService) {
        this.tagCtrl.valueChanges.pipe(takeUntil(this.cleanupTime))
        .subscribe(() => {
            const val = this.tagCtrl.value?.toLowerCase ? this.tagCtrl.value.toLowerCase() : '';
            this.filteredTags = _.filter(this.availableTags(), tag => tag.label.toLowerCase().includes(val));
        });
    }

    selected(event: MatAutocompleteSelectedEvent): void {
        this.topic.tags.push(event.option.value.id);
        this.tagInput.nativeElement.value = '';
        this.tagCtrl.setValue(null);
        this.changed.emit();
    }

    removeTag(tagOrId): void {
        if (tagOrId.isNew) {
            this.topic.newTags.splice(this.topic.newTags.indexOf(tagOrId), 1);
        } else {
            this.topic.tags.splice(this.topic.tags.indexOf(tagOrId), 1);
        }
        this.changed.emit();
    }

    availableTags(): any[] {
        return _.filter(this.streamTags, tag => !_.includes(this.topic.tags, tag.id));
    }

    add(event: MatChipInputEvent): void {
        if (event.value?.trim().length) {
            const existingMatch = this.availableTags().find(tag => tag.label.toLowerCase() === event.value.toLocaleLowerCase());
            
            if (existingMatch) {
                if (!this.topic.tags.find(tagId => tagId === existingMatch.id)) {
                    this.topic.tags.push(existingMatch.id);
                    this.changed.emit();
                }
            } else {
                this.topic.newTags = this.topic.newTags || [];
                this.topic.newTags.push({
                    isNew: true,
                    label: event.value
                });
            }

            if (event.input) {
                event.input.value = '';
            }

            this.tagCtrl.setValue(null);
        }
    }

    get currentTags(): any[] {
        return [].concat(this.topic?.tags, this.topic?.newTags).filter(it => it);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.tagsUpdatedAt || changes.catalogId) {
            this.streamTags = undefined;   
        }

        const tagFetch = this.isShared ? this.tenantService.getCatalogTags() : this.tenantService.getStreamTags();

        (this.streamTags ? of(this.streamTags) : tagFetch)
        .pipe(tap(streamTags => this.streamTags = streamTags))
        .subscribe(() => this.tagMap = _.keyBy(this.streamTags, tag => tag.id));
    }

    ngOnDestroy(): void {
        this.cleanupTime.next();
        this.cleanupTime.complete();
    }

}
